import * as spine from "@esotericsoftware/spine-webgl";
const { Physics } = spine;
import { useConfigStore } from "../store/configStore";
import { useEditorStore } from "../store/editorStore";
import { MultiAtlasAttachmentLoader } from "./multiAtlasLoader";
import { buildTwoColorTint, collectToningSchemes } from "./toning";
import { getTemplateOrder } from "../types/spine-template";
import { skelDataToJsonCompat, parseSpineToConfig } from "./spineParser";
import { getFixedCameraBounds } from "../utils/fullscreenType";

export interface SkeletonInstance {
  id: string;
  skeleton: spine.Skeleton;
  state: spine.AnimationState;
  isMain: boolean;
  type?: string;
}

interface SpinePreviewAppOptions {
  /** 主模版 Spine 数据；无 Zip 的静态主模版可为 null，仅渲染已加载的关联模版 */
  spineData: any | null;
  relatedTemplates?: any[];
  /** 与模版数据一致：同类型内越靠后的 id 渲染层级越高 */
  relatedTemplateIdsOrder?: string[];
  setError: (error: string | null) => void;
  cameraMode?: "fixed" | "dynamic";
  /** 0 = 1:1（512），1 = 9:16 资源坐标系（912，出图时再居中裁切） */
  fullscreenType?: number | null;
}

export class SpinePreviewApp {
  // 存储所有 Skeleton 实例
  instances: SkeletonInstance[] = [];

  // skinName -> { skin: spine.Skin, templateId: string }
  skinIndex = new Map<string, { skin: spine.Skin; templateId: string }>();

  // skinName -> slotNames[] 映射
  skinSlots = new Map<string, { slotNames: string[]; templateId: string }>();

  bounds = {
    offset: new spine.Vector2(),
    size: new spine.Vector2(),
  };

  spineCanvas: spine.SpineCanvas | null = null;
  spineData: any | null;
  relatedTemplates: any[] | undefined;
  /** 关联模版绘制次序（同类型内与数组下标一致） */
  relatedTemplateIdsOrder: string[];
  setError: (error: string | null) => void;

  // 状态追踪
  lastSelectedSkinsKey: string = "";
  lastAnimation: string | null = null;
  lastToningColorsKey: string = "";
  lastToningsLength: number = 0;
  updateCount = 0;
  lastLogTime = 0;

  cameraMode: "fixed" | "dynamic" = "fixed";
  boundsFixed = false;
  fullscreenType: number = 0;

  constructor(options: SpinePreviewAppOptions) {
    this.spineData = options.spineData;
    this.relatedTemplates = options.relatedTemplates;
    this.relatedTemplateIdsOrder = options.relatedTemplateIdsOrder ?? [];
    this.setError = options.setError;
    this.cameraMode = options.cameraMode || "fixed";
    this.fullscreenType = options.fullscreenType === 1 ? 1 : 0;

    console.log(
      `[SpinePreviewApp] Initialized (${this.cameraMode}, fullscreenType=${this.fullscreenType}) with relatedTemplates:`,
      this.relatedTemplates?.length,
    );
  }

  loadAssets(canvas: spine.SpineCanvas) {
    const assetManager = canvas.assetManager;

    // 1. 加载主模版资源（静态主模版可无 spineData，仅关联 Spine）
    if (this.spineData) {
      // 根据 skelBytes 是否存在决定加载方式
      if (this.spineData.skelBytes) {
        // .skel 二进制：需要转为 base64 data URI（Spine 的 loadBinary 会从中提取 base64 解码）
        const skelBytes = this.spineData.skelBytes as Uint8Array;
        let binary = "";
        for (let i = 0; i < skelBytes.byteLength; i++) {
          binary += String.fromCharCode(skelBytes[i]);
        }
        const base64 = btoa(binary);
        assetManager.setRawDataURI(
          "skeleton.skel",
          "data:application/octet-stream;base64," + base64,
        );
        assetManager.loadBinary("skeleton.skel");
      } else {
        // .json
        assetManager.setRawDataURI(
          "skeleton.json",
          "data:application/json;base64," +
            btoa(
              unescape(encodeURIComponent(JSON.stringify(this.spineData.json))),
            ),
        );
        assetManager.loadJson("skeleton.json");
      }

      const pngDataUrls = this.spineData.pngDataUrls as Record<string, string>;
      for (const [fileName, dataUrl] of Object.entries(pngDataUrls)) {
        assetManager.setRawDataURI(fileName, dataUrl);
      }

      const atlasTexts = this.spineData.atlasTexts as Record<string, string>;
      for (const [atlasFileName, atlasText] of Object.entries(atlasTexts)) {
        assetManager.setRawDataURI(
          atlasFileName,
          "data:text/plain;base64," +
            btoa(unescape(encodeURIComponent(atlasText))),
        );
        assetManager.loadTextureAtlas(atlasFileName);
      }
    }

    // 2. 加载关联模版资源
    if (this.relatedTemplates) {
      console.log(
        "[SpinePreviewApp] Loading assets for related templates:",
        this.relatedTemplates.length,
      );
      for (const rel of this.relatedTemplates) {
        const relData = rel.spineData;
        if (!relData || (!relData.json && !relData.skelBytes)) {
          continue;
        }
        // 与主模版一致：.skel 必须走 loadBinary，占位 JSON 无法被 SkeletonJson 正确还原
        if (relData.skelBytes) {
          const skelUri = `${rel.id}_skeleton.skel`;
          const skelBytes = relData.skelBytes as Uint8Array;
          let binary = "";
          for (let i = 0; i < skelBytes.byteLength; i++) {
            binary += String.fromCharCode(skelBytes[i]);
          }
          assetManager.setRawDataURI(
            skelUri,
            "data:application/octet-stream;base64," + btoa(binary),
          );
          assetManager.loadBinary(skelUri);
        } else {
          const jsonUri = `${rel.id}_skeleton.json`;
          assetManager.setRawDataURI(
            jsonUri,
            "data:application/json;base64," +
              btoa(unescape(encodeURIComponent(JSON.stringify(relData.json)))),
          );
          assetManager.loadJson(jsonUri);
        }

        // PNGs
        if (relData.pngDataUrls) {
          for (const [fileName, dataUrl] of Object.entries(
            relData.pngDataUrls,
          )) {
            assetManager.setRawDataURI(fileName, dataUrl as string);
          }
        }
        // Atlases
        if (relData.atlasTexts) {
          for (const [atlasFileName, atlasText] of Object.entries(
            relData.atlasTexts,
          )) {
            const uniqueAtlasName = `${rel.id}_${atlasFileName}`;
            assetManager.setRawDataURI(
              uniqueAtlasName,
              "data:text/plain;base64," +
                btoa(unescape(encodeURIComponent(atlasText as string))),
            );
            assetManager.loadTextureAtlas(uniqueAtlasName);
          }
        }
      }
    }
  }

  initialize(canvas: spine.SpineCanvas) {
    this.spineCanvas = canvas;
    const assetManager = canvas.assetManager;

    try {
      // --- 初始化主模版（无主 Zip 的静态模版跳过）---
      if (this.spineData) {
        const mainAtlasTexts = this.spineData.atlasTexts as Record<
          string,
          string
        >;
        const mainAtlasFileNames = Object.keys(mainAtlasTexts);
        const skelUri = this.spineData.skelBytes ? "skeleton.skel" : "skeleton.json";
        const isBinary = !!this.spineData.skelBytes;
        const mainSkeleton = this.createSkeleton(
          assetManager,
          skelUri,
          mainAtlasFileNames,
          isBinary,
        );

        const { templateType } = useConfigStore.getState();

        this.instances.push({
          id: "main",
          skeleton: mainSkeleton,
          state: this.createAnimationState(mainSkeleton),
          isMain: true,
          type: templateType || "character",
        });

        this.buildSkinIndex(mainSkeleton.data, "main");

        // .skel 模式：SpinePreviewApp 拥有真实 atlas，解析后从 SkeletonData 提取真实数据并回填 store
        if (this.spineData.skelBytes && this.spineData.json?._pendingSkelParse) {
          const realJsonCompat = skelDataToJsonCompat(mainSkeleton.data);
          console.log("[SpinePreviewApp] .skel parsed, backfilling store with real data", {
            skins: realJsonCompat.skins?.length,
            animations: Object.keys(realJsonCompat.animations || {}).length,
          });

          // 更新 spineData.json 为真实数据
          this.spineData.json = realJsonCompat;
          // 更新 store（触发 parseSkins 等下游逻辑）
          const { setSpineData, templateConfig, setTemplateConfig } = useConfigStore.getState();
          setSpineData({ ...this.spineData });

          // 用真实数据更新 templateConfig（动画列表等）
          const baseConfig = parseSpineToConfig(realJsonCompat);
          if (templateConfig) {
            setTemplateConfig({
              ...templateConfig,
              animations: baseConfig.animations,
            });
          } else {
            setTemplateConfig(baseConfig);
            setTimeout(() => {
              const { randomizeSkins } = useEditorStore.getState();
              randomizeSkins();
            }, 0);
          }
        }

        this.buildSkinSlots(this.spineData.json, "main");
      }

      // --- 初始化关联模版 ---
      if (this.relatedTemplates) {
        console.log(
          "[SpinePreviewApp] Initializing related skeletons:",
          this.relatedTemplates.length,
        );
        for (const rel of this.relatedTemplates) {
          const rs = rel.spineData;
          if (!rs || (!rs.json && !rs.skelBytes)) {
            continue;
          }
          const relAtlasNames: string[] = [];
          if (rs.atlasTexts) {
            for (const name of Object.keys(rs.atlasTexts)) {
              relAtlasNames.push(`${rel.id}_${name}`);
            }
          }

          try {
            const isRelBinary = !!rs.skelBytes;
            const relSkelUri = isRelBinary
              ? `${rel.id}_skeleton.skel`
              : `${rel.id}_skeleton.json`;
            const relSkeleton = this.createSkeleton(
              assetManager,
              relSkelUri,
              relAtlasNames,
              isRelBinary,
            );

            if (rs.skelBytes && rs.json?._pendingSkelParse) {
              rs.json = skelDataToJsonCompat(relSkeleton.data);
            }

            this.instances.push({
              id: rel.id,
              skeleton: relSkeleton,
              state: this.createAnimationState(relSkeleton),
              isMain: false,
              type: rel.type,
            });

            this.buildSkinIndex(relSkeleton.data, rel.id);
            this.buildSkinSlots(rs.json, rel.id);
          } catch (e) {
            console.error(`Failed to initialize related template ${rel.id}`, e);
          }
        }
      }

      if (this.cameraMode === "dynamic") {
        // 动态模式：初始先做一次计算（无主模版时用第一个关联实例）
        const mainInst =
          this.instances.find((i) => i.isMain) ?? this.instances[0];
        if (mainInst) {
          mainInst.skeleton.updateWorldTransform(Physics.update);
          mainInst.skeleton.getBounds(this.bounds.offset, this.bounds.size);
        }
      } else {
        // 固定模式：原点在底部中心；1:1=512，9:16 资源=912（zip 仍是正方形）
        const fixed = getFixedCameraBounds(this.fullscreenType);
        this.bounds.offset.set(fixed.offsetX, fixed.offsetY);
        this.bounds.size.set(fixed.width, fixed.height);
      }

      // 设置初始状态
      this.updateSkinsInternal();
    } catch (err) {
      console.error("Spine 初始化失败:", err);
      this.setError(err instanceof Error ? err.message : "初始化失败");
    }
  }

  createSkeleton(
    assetManager: spine.AssetManager,
    dataUri: string,
    atlasNames: string[],
    isBinary: boolean = false,
  ) {
    let atlasLoader: spine.AttachmentLoader;
    if (atlasNames.length === 1) {
      const atlas = assetManager.require(atlasNames[0]) as spine.TextureAtlas;
      atlasLoader = new spine.AtlasAttachmentLoader(atlas);
    } else {
      const atlases = atlasNames.map((fileName) =>
        assetManager.require(fileName),
      ) as spine.TextureAtlas[];
      atlasLoader = new MultiAtlasAttachmentLoader(atlases);
    }

    let skeletonData: spine.SkeletonData;
    if (isBinary) {
      const skeletonBinary = new spine.SkeletonBinary(atlasLoader);
      skeletonData = skeletonBinary.readSkeletonData(
        assetManager.require(dataUri),
      );
    } else {
      const skeletonJson = new spine.SkeletonJson(atlasLoader);
      skeletonData = skeletonJson.readSkeletonData(
        assetManager.require(dataUri),
      );
    }

    return new spine.Skeleton(skeletonData);
  }

  createAnimationState(skeleton: spine.Skeleton) {
    const stateData = new spine.AnimationStateData(skeleton.data);
    stateData.defaultMix = 0.2;
    const state = new spine.AnimationState(stateData);

    // 设置默认动画
    const { currentAnimation: animName } = useEditorStore.getState();

    let defaultAnim = null;

    // 1. 尝试使用当前选中的动画 (如果 Skeleton 中存在)
    if (animName && skeleton.data.findAnimation(animName)) {
      defaultAnim = animName;
    }

    // 2. 如果不存在，尝试 fallback 到 idle_default
    if (!defaultAnim) {
      defaultAnim = skeleton.data.findAnimation("idle_default")?.name;
    }

    // 3. 实在不行，用第一个动画
    if (!defaultAnim) {
      defaultAnim = skeleton.data.animations[0]?.name;
    }

    if (defaultAnim) {
      state.setAnimation(0, defaultAnim, true);
    }
    return state;
  }

  buildSkinIndex(skeletonData: spine.SkeletonData, templateId: string) {
    for (const skin of skeletonData.skins) {
      // 存储时带上 templateId
      this.skinIndex.set(skin.name, { skin, templateId });
    }
  }

  buildSkinSlots(jsonData: any, templateId: string) {
    const skins = jsonData.skins || [];
    for (const skin of skins) {
      if (skin.attachments) {
        const slotNames = Object.keys(skin.attachments);
        this.skinSlots.set(skin.name, { slotNames, templateId });
      }
    }
  }

  update(_canvas: spine.SpineCanvas, delta: number) {
    void _canvas;
    if (this.instances.length === 0) return;

    this.updateCount++;
    const now = performance.now();
    if (now - this.lastLogTime > 1000) {
      if (this.updateCount > 70) {
        // console.warn(`⚠️ SpineApp.update frequency: ${this.updateCount}/s`);
      }
      this.updateCount = 0;
      this.lastLogTime = now;
    }

    const configStore = useConfigStore.getState();
    const editorStore = useEditorStore.getState();
    const currentSkins = editorStore.selectedSkins;
    const currentAnimation = editorStore.currentAnimation;
    const currentToningColors = editorStore.selectedToningColors;
    const previewToningId = editorStore.previewToningId;
    const tonings = collectToningSchemes(
      configStore.templateConfig,
      this.relatedTemplates,
    );

    // Check Skins
    const skinsKey = currentSkins.join(",");
    if (skinsKey !== this.lastSelectedSkinsKey) {
      this.lastSelectedSkinsKey = skinsKey;
      this.updateSkinsInternal();
    }

    // Check Animation
    if (currentAnimation !== this.lastAnimation) {
      this.lastAnimation = currentAnimation;
      this.updateAnimationInternal();
    }

    // Check Toning
    const toningColorsKey = `${previewToningId || ""}|${Object.entries(currentToningColors)
      .map(([toningId, colorSetId]) => {
        const toning = tonings.find((t: any) => t.id === toningId);
        const colorSet = toning?.colors?.find((c: any) => c.id === colorSetId);
        let colorsHash = "";
        if (colorSet?.light && colorSet?.dark) {
          colorsHash = `${colorSet.light}|${colorSet.dark}`;
        } else if (colorSet?.colors) {
          colorsHash = colorSet.colors
            .map((c: any) => `${c.color}@${c.offset}`)
            .join("|");
        }
        return `${toningId}:${colorSetId}:${colorsHash}`;
      })
      .join(",")}`;
    const toningChanged =
      toningColorsKey !== this.lastToningColorsKey ||
      tonings.length !== this.lastToningsLength;
    if (toningChanged) {
      this.lastToningColorsKey = toningColorsKey;
      this.lastToningsLength = tonings.length;
      this.applyToning();
    }

    // Update all instances
    for (const inst of this.instances) {
      inst.skeleton.update(delta);
      inst.state.update(delta);
      inst.state.apply(inst.skeleton);
      inst.skeleton.updateWorldTransform(Physics.update);
    }
  }

  updateSkinsInternal() {
    const { selectedSkins: currentSelectedSkins } = useEditorStore.getState();

    // 为每个 Instance 准备一个新的 Custom Skin
    const newSkins = new Map<string, spine.Skin>();
    for (const inst of this.instances) {
      newSkins.set(inst.id, new spine.Skin("custom"));
    }

    const reversedSkins = [...currentSelectedSkins].reverse();
    for (const skinName of reversedSkins) {
      const info = this.skinIndex.get(skinName);
      if (info) {
        const customSkin = newSkins.get(info.templateId);
        if (customSkin) {
          customSkin.addSkin(info.skin);
        }
      }
    }

    // 应用 Skin 到每个 Skeleton
    for (const inst of this.instances) {
      const customSkin = newSkins.get(inst.id);
      if (customSkin) {
        inst.skeleton.setSkin(customSkin);
        inst.skeleton.setSlotsToSetupPose();
        inst.skeleton.updateCache();
      }
    }

    this.applyToning();

    // 如果是动态模式且尚未锁定边界，更新边界
    if (this.cameraMode === "dynamic" && !this.boundsFixed) {
      const mainInst =
        this.instances.find((i) => i.isMain) ?? this.instances[0];
      if (mainInst) {
        mainInst.skeleton.updateWorldTransform(Physics.reset);
        mainInst.skeleton.getBounds(this.bounds.offset, this.bounds.size);

        // 如果 bounds 有效（size不为0），标记为已固定
        if (this.bounds.size.x > 0 && this.bounds.size.y > 0) {
          this.boundsFixed = true;
        }
      }
    }
  }

  updateAnimationInternal() {
    const { currentAnimation: animName } = useEditorStore.getState();
    if (!animName) return;

    for (const inst of this.instances) {
      const animation = inst.skeleton.data.findAnimation(animName);
      if (animation) {
        inst.state.setAnimation(0, animName, true);
        inst.skeleton.updateWorldTransform(Physics.reset);
      }
    }
  }

  applyToning() {
    if (this.instances.length === 0) return;

    const { templateConfig: config } = useConfigStore.getState();
    const {
      selectedSkins: currentSelectedSkins,
      selectedToningColors: toningColors,
      previewToningId,
    } = useEditorStore.getState();

    if (!config) return;

    // 构建全局 SKU 映射: skuId -> { skuData, templateId }
    const allSkus = new Map<string, { skuData: any; templateId: string }>();

    // 主模版 SKU
    if (config.skus) {
      for (const [skuId, sku] of Object.entries(config.skus)) {
        allSkus.set(skuId, { skuData: sku, templateId: "main" });
      }
    }

    // 关联模版 SKU
    if (this.relatedTemplates) {
      for (const rel of this.relatedTemplates) {
        if (rel.config && rel.config.skus) {
          for (const [skuId, sku] of Object.entries(rel.config.skus)) {
            allSkus.set(skuId, { skuData: sku, templateId: rel.id });
          }
        }
      }
    }

    const tonings = collectToningSchemes(config, this.relatedTemplates);

    // 辅助函数：根据 skinName 找到对应的 SKU
    const getSkuInfoForSkin = (skinName: string) => {
      for (const [skuId, info] of allSkus.entries()) {
        if (info.skuData.skinName === skinName) {
          return { skuId, ...info };
        }
      }
      return null;
    };

    // 重置颜色
    for (const inst of this.instances) {
      for (const slot of inst.skeleton.slots) {
        slot.color.set(1, 1, 1, 1);
        if (slot.darkColor) slot.darkColor.set(0, 0, 0, 1);
      }
    }

    // 应用染色
    for (const skinName of currentSelectedSkins) {
      const skuInfo = getSkuInfoForSkin(skinName);
      if (!skuInfo) continue;
      const { skuData, templateId } = skuInfo;
      const toningIds: string[] = skuData.toningIds || [];
      const instance = this.instances.find((i) => i.id === templateId);
      if (!instance) continue;
      const skinSlotInfo = this.skinSlots.get(skinName);
      if (!skinSlotInfo) continue;

      const orderedToningIds =
        previewToningId && toningIds.includes(previewToningId)
          ? [previewToningId, ...toningIds.filter((id) => id !== previewToningId)]
          : toningIds;

      for (const toningId of orderedToningIds) {
        const colorId = toningColors[toningId];
        if (!colorId) continue;
        const toning = tonings.find((t: any) => t.id === toningId);
        if (!toning) continue;
        const tint = buildTwoColorTint(toning, colorId);
        if (!tint) continue;

        for (const slotName of skinSlotInfo.slotNames) {
          const slot = instance.skeleton.slots.find(
            (s) => s.data.name === slotName,
          );
          if (slot) {
            slot.color.set(
              tint.light.r,
              tint.light.g,
              tint.light.b,
              tint.light.a,
            );
            if (!slot.data.darkColor)
              slot.data.darkColor = new spine.Color(0, 0, 0, 1);
            if (!slot.darkColor) slot.darkColor = new spine.Color(0, 0, 0, 1);
            slot.darkColor.set(
              tint.dark.r,
              tint.dark.g,
              tint.dark.b,
              tint.dark.a,
            );
          }
        }
        break;
      }
    }
  }

  render(canvas: spine.SpineCanvas) {
    if (this.instances.length === 0) return;

    const renderer = canvas.renderer;
    const camera = renderer.camera;
    const htmlCanvas = canvas.htmlCanvas;

    renderer.resize(1);

    const offset = this.bounds.offset;
    const size = this.bounds.size;
    camera.position.x = offset.x + size.x / 2;
    camera.position.y = offset.y + size.y / 2;

    const zoomX = size.x / htmlCanvas.width;
    const zoomY = size.y / htmlCanvas.height;
    camera.zoom = Math.max(zoomX, zoomY);
    camera.update();

    canvas.clear(0, 0, 0, 0);
    renderer.begin();

    const indexInRelatedOrder = (templateId: string) => {
      const i = this.relatedTemplateIdsOrder.indexOf(templateId);
      return i === -1 ? this.relatedTemplateIdsOrder.length + 1 : i;
    };

    // 渲染顺序：先按 TEMPLATE_ORDER，同类型再按 relatedTemplateIds 中下标（越大越靠上）
    const sortedInstances = [...this.instances].sort((a, b) => {
      const orderA = getTemplateOrder(a.type, a.isMain);
      const orderB = getTemplateOrder(b.type, b.isMain);
      if (orderA !== orderB) return orderA - orderB;
      const tieA = a.isMain ? -1 : indexInRelatedOrder(a.id);
      const tieB = b.isMain ? -1 : indexInRelatedOrder(b.id);
      return tieA - tieB;
    });

    for (const inst of sortedInstances) {
      renderer.drawSkeleton(inst.skeleton, false);
    }

    renderer.end();
  }
}
